import React, { useMemo, useState } from 'react';
import { GenerateIcon, ICONS, Popover, PopoverContent, PopoverTrigger } from '@pileus-cloud/anodot-frontend-common';
import SwitchButton from 'shared/components/andtComponents/Switch';
import Spinner, { SPINNER_SIZES } from 'shared/components/andtComponents/Spinner';
import Button from 'shared/components/andtComponents/Button';
import { PERMISSION_TYPES } from '../../consts';
import SharedEntitiesModal from './SharedItemsModal/SharedEntitiesModal';
import PermissionTooltip from './PermissionTooltip';
import { getNewCategoryPermissions } from 'users/containers/Organization/components/EffectivePermissions/effectivePremissionsUtils.js';
import useRoles from 'users/new-user-management/hooks/reactQuery/useRoles';

import styles from './Permission.module.scss';

const Permission = ({
  actionId,
  allRolePermissions,
  categoryPermissions,
  isViewOnly,
  onPermissionChange,
  roleId,
  userId,
}) => {
  const [isSharedEntitiesModalOpen, setIsSharedEntitiesModalOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { setRolePermissions } = useRoles();

  const category = categoryPermissions?.category;
  const permissions = categoryPermissions?.actionPermissions?.find((p) => p.action === actionId)?.permissions;
  const isAllowed = categoryPermissions?.actionPermissions?.find((p) => p.action === actionId);

  const handleMouseEnter = () => {
    setIsTooltipOpen(true);
  };

  const handleMouseLeave = () => {
    setIsTooltipOpen(false);
  };

  const permissionTypeAndRoles = useMemo(() => {
    let maxPermissionType = PERMISSION_TYPES.NONE.id;
    const fullPermissionRoles = [];
    permissions?.forEach((permission) => {
      if (permission.permissionType === PERMISSION_TYPES.FULL.id) {
        if (permission.derivedFromRole) {
          fullPermissionRoles.push(permission.derivedFromRole);
        }
        maxPermissionType = PERMISSION_TYPES.FULL.id;
      }
      if (permission.permissionType === PERMISSION_TYPES.PARTIAL.id && maxPermissionType !== PERMISSION_TYPES.FULL.id) {
        maxPermissionType = PERMISSION_TYPES.PARTIAL.id;
      }
    });
    return {
      type: maxPermissionType,
      roles: maxPermissionType === PERMISSION_TYPES.FULL.id ? fullPermissionRoles : null,
    };
  }, [permissions]);

  const permissionChangedHandler = (newPermission) => {
    let categoryNewPermissions = getNewCategoryPermissions(
      allRolePermissions,
      categoryPermissions,
      actionId,
      newPermission,
    );
    // If onPermissionsChange is defined, it means that this component is from EffectivePermissionsStep, and the
    // calculations there are a bit different than in the EffectivePermissionsTab and not saved right away.
    if (onPermissionChange) {
      onPermissionChange(categoryNewPermissions);
    } else {
      setRolePermissions.mutate({
        roleId: roleId,
        roleCategoryPermissions: [categoryNewPermissions],
      });
    }
  };

  return !isAllowed ? null : (
    <>
      {permissionTypeAndRoles.type === PERMISSION_TYPES.FULL.id && (
        <>
          {permissionTypeAndRoles.roles?.length === 0 ? (
            <>
              {setRolePermissions.isPending ? (
                <div className={'position-relative'}>
                  <Spinner size={SPINNER_SIZES.SMALL} />
                </div>
              ) : (
                <SwitchButton
                  isDisabled={isViewOnly}
                  onChange={permissionChangedHandler}
                  isChecked
                  className={styles.permissionSwitch}
                  isSmall
                />
              )}
            </>
          ) : (
            <Popover open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
              <PopoverTrigger onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                <GenerateIcon iconName={ICONS.check.name} className={styles.permissionIcon} />
              </PopoverTrigger>
              {permissionTypeAndRoles?.roles?.length > 0 ? (
                <PopoverContent className="z-1">
                  <PermissionTooltip roles={permissionTypeAndRoles.roles} />
                </PopoverContent>
              ) : null}
            </Popover>
          )}
        </>
      )}
      {permissionTypeAndRoles.type === PERMISSION_TYPES.NONE.id && (
        <>
          {setRolePermissions.isPending ? (
            <div className={'position-relative'}>
              <Spinner size={SPINNER_SIZES.SMALL} />
            </div>
          ) : (
            <SwitchButton
              onChange={permissionChangedHandler}
              isChecked={false}
              isDisabled={isViewOnly}
              className={styles.permissionSwitch}
              isSmall
            />
          )}
        </>
      )}
      {permissionTypeAndRoles.type === PERMISSION_TYPES.PARTIAL.id && (
        <>
          <Button
            text="Shared Items"
            onClick={() => setIsSharedEntitiesModalOpen(!isSharedEntitiesModalOpen)}
            className={{ buttonText: styles.sharedItemsText }}
            isTextButton
          />
          <>
            {setRolePermissions.isPending ? (
              <div className={'position-relative'}>
                <Spinner size={SPINNER_SIZES.SMALL} />
              </div>
            ) : (
              <SwitchButton
                onChange={permissionChangedHandler}
                isChecked={false}
                className={styles.permissionSwitch}
                isSmall
                isDisabled={!!userId || isViewOnly}
              />
            )}
          </>
          {isSharedEntitiesModalOpen && (
            <SharedEntitiesModal
              actionId={actionId}
              category={category}
              isOpen={isSharedEntitiesModalOpen}
              userId={userId}
              roleId={roleId}
              setIsOpen={setIsSharedEntitiesModalOpen}
            />
          )}
        </>
      )}
    </>
  );
};


export default Permission;
